import api from '../../../services/api';

export const MAX_COST_AMOUNT = 1000000000;
export const CUSTOMER_ORDER_COST_CATEGORY = 'OTHER';

function cloneFormData(source: FormData): FormData {
  const next = new FormData();

  source.forEach((value, key) => {
    next.append(key, value);
  });

  return next;
}

export interface SaveOrderCostInput {
  orderId: string;
  costId?: string;
  category?: string;
  values: Record<string, any>;
}

export function isManualCustomerOrderCost(cost?: { category?: string; transportId?: string | null } | null): boolean {
  return Boolean(
    cost?.category
    && cost.category !== 'PRODUCT_COST'
    && !cost.transportId,
  );
}

export function isManagedTransportCost(cost?: { transportId?: string | null } | null): boolean {
  return Boolean(cost?.transportId);
}

/**
 * Customer-order operational costs are every order-scoped cost except the
 * goods-purchase rows rendered in the separate purchase-invoice table.
 *
 * Historical orders can legitimately contain categories such as
 * CUSTOMS_AGENCY or BORDER_TAX. Restricting this view to the modern OTHER
 * fallback category hides real costs while the margin still deducts them.
 */
export function isOperationalCustomerOrderCost(
  cost?: { category?: string } | null,
): boolean {
  return Boolean(cost?.category) && cost?.category !== 'PRODUCT_COST';
}

function getUploadedFile(fileList?: any[]) {
  const file = fileList?.[0];
  return file?.originFileObj || file?.file || null;
}

function buildOrderCostFormData(values: Record<string, any>, category = CUSTOMER_ORDER_COST_CATEGORY): FormData {
  const payload = new FormData();
  payload.append('category', category);

  ['currency', 'amount', 'description'].forEach((key) => {
    if (values[key] !== undefined && values[key] !== null) {
      payload.append(key, values[key]);
    }
  });

  const uploadedFile = getUploadedFile(values.document);
  if (uploadedFile) {
    payload.append('document', uploadedFile);
  }

  return payload;
}

export async function saveOrderCost({ orderId, costId, category, values }: SaveOrderCostInput): Promise<void> {
  const payload = cloneFormData(buildOrderCostFormData(values, category));

  if (costId) {
    await api.put(`/costs/${costId}`, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return;
  }

  payload.append('orderId', orderId);
  await api.post('/costs', payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function deleteOrderCost(costId: string): Promise<void> {
  await api.delete(`/costs/${costId}`);
}
